import { ecorEnv } from '@/../environments/environment';
import { AppService } from '@/app.service';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { AppSettings, AppSettingsService } from '@/common/services/app-settings.service';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { CaseListService } from '@/support/states/services';
import { WINDOW } from '@/window';
import { Component, Inject, OnDestroy, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { RouterModule } from '@angular/router';
// updated for angular v18 upgrade
@Component({
    selector: 'app-ecorr-nav',
    templateUrl: './ecorr-nav.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatBadgeModule,
        MatInputModule,
        MatIconModule,
        MatToolbarModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        RouterModule
    ],
    providers: [
    ]
})
export class EcorrNavComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);
    private store = inject(Store<PortalUserState>);
    private appService = inject(AppService);
    private appSettingsService = inject(AppSettingsService);
    private paradataEventService = inject(ParadataEventService);
    private ecorrUtilsService = inject(EcorrUtilsService);
    private caseListService = inject(CaseListService);
    private window = inject(WINDOW);
    
    public router = inject(Router);
    
    readonly OPEN_STATUS = 'Opened/Read';
    
    // Signal-based state management
    portalUser = signal<Observable<PortalUser> | null>(null);
    settings = signal<AppSettings>({ pegaIntegration: true });
    user = signal<PortalUser | null>(null);
    messageError = signal<boolean>(false);

    // Computed properties
    currentUser = computed(() => this.user());
    currentSettings = computed(() => this.settings());
    hasMessageError = computed(() => this.messageError());
    currentUnreadMessages = computed(() => this.caseListService.unreadMessages());

    constructor() {
        this.initializeComponent();
    }

    private initializeComponent(): void {
        this.getPortalUser();

        this.store.select(portalUserSelector)
            .pipe(
                distinctUntilChanged((prev, curr) =>
                    prev?.email === curr?.email &&
                    prev?.activeSurveyCards === curr?.activeSurveyCards
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(user => {
                this.user.set(user);
            });

        this.store.select(portalUserSelector)
            .pipe(
                distinctUntilChanged((prev, curr) => prev?.email === curr?.email),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(user => {
                if (user?.email) {
                    this.getCases();
                }
            });
    }
    
    ngOnInit(): void {
        setTimeout(() => {
            this.appService.loading = false;
        }, 3000);
    }

    private getCases(): void {
        const currentUser = this.user();
        if (!currentUser?.email) return;

        this.caseListService.getCasesByEmail(currentUser.email)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: any) => {
                    const unread = data.pxResults?.filter(
                        (message: any) => message.AccessStatus !== this.OPEN_STATUS
                    ).length || 0;

                    this.caseListService.unreadMessages.set(unread);
                    const readIds = data.pxResults
                        ?.filter((message: any) => message.AccessStatus == this.OPEN_STATUS)
                        .map((message: any) => message.CaseID);
                    this.caseListService.readCaseIds = new Set<string>(readIds);
                    this.messageError.set(false);
                },
                error: () => {
                    this.messageError.set(true);
                }
            });
    }

    private getPortalUser(): void {
        let settingsRetrieved = false;
        this.portalUser.set(this.store.select(portalUserSelector));
        
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(observer => {
                // PortalUser must exist before accessing secure endpoints
                if (!settingsRetrieved && observer && observer.userAccountId > 0) {
                    this.getAppSettings();
                    settingsRetrieved = true;
                }
            });
    }

    private getAppSettings(): void {
        const settingslessRoutes = ['/portal', '/faq'];
        const currentPath = this.window.location.pathname;

        if (!settingslessRoutes.includes(currentPath)) {
            this.appSettingsService.getAppSettings()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(settings => {
                    if (typeof settings === 'string') {
                        // Manually retyping to string then removing tainting and reparsing
                        const parsedSettings = JSON.parse(
                            JSON.stringify(settings).slice(8, -1).replace(/\\/g, '')
                        );
                        this.settings.set(parsedSettings);
                    }
                });
        }
    }

    ngOnDestroy(): void {
        // No need for manual subscription cleanup with takeUntilDestroyed
        // The destroyRef will automatically handle cleanup
    }

    logout(): void {
        this.ecorrUtilsService.logout();
    }

    sendMessage(): void {
        this.paradataEventService.add(ParadataEventEnum.Message);

        this.router.navigate(['/submit-case'], {
            queryParams: {
                surveyInstanceId: -102030405
            }
        });
    }

    public showAccountSettings(): void {
        this.paradataEventService.add(ParadataEventEnum.ManageAccount);
        if (ecorEnv.cpassEnabled) {
            this.window.location.href = ecorEnv.accountSettingsUrl;
        }
    }
}
